import {ReactElement, useMemo, useState} from 'react';
import {Typography} from '@mui/material';
import {t} from 'i18next';
import _ from 'lodash';
import AurAccordion from '../../../common/aur-accordion/AurAccordion';
import {ReactComponent as ExportIcon} from '../../../../assests/svg/export_icon.svg';
import {ReactComponent as ResetIcon} from '../../../../assests/svg/reset.svg';
import {ReactComponent as ProcessingIcon} from '../../../../assests/svg/processing.svg';
import {ReactComponent as RightArrowIcon} from '../../../../assests/svg/right-icon.svg';
import {ReactComponent as CompletedIcon} from '../../../../assests/svg/complete.svg';
import {AurDropdownButton} from '../../../common/aur-button/AurDropdownButton';
import AurButton from '../../../common/aur-button/AurButton';
import AurPizzaTrackerProgress, {
  DataSetReportStatusItem,
  STEP_PROCESSING_STATUS,
} from '../../../common/aur-pizza-traker-progress/AurPizzaTrackerProgress';
import {useWindowSize} from '../../../../hooks/utils/useWindowSize';
import {
  downloadResponseAsCSV,
  getUTCDateTimeForDisplay,
} from '../../../../utils/utils';
import {useDispatch, useSelector} from 'react-redux';
import {setWorkspaceDataSet} from '../../../../redux/slices/reportsSlice';
import {RootState} from '../../../../redux/store';
import {ReportScreenTabTypes} from '../../../pages/reports/ProjectOverview';
import {
  downloadDataSetProcessingProgressCSV,
  getDatasetProcessingProgressReport,
} from '../../../../api/ReportingManagement';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import {showErrorToast} from '../../../../api/utils';
import AurPopup from '../../../common/aur-popup/AurPopup';
import {AurLightTooltip} from '../../../common/aur-light-tooltip/aur-light-tooltip';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';

// Type of identified current status of data set progress
type PROCESSING_STATUS = 'Queued' | 'Processing' | 'Filtering' | 'Exporting';

// This use to display every progres status in particulor sequance
const PROCESSING_STEP_DISPLAY_SEQUENCE: PROCESSING_STATUS[] = [
  'Queued',
  'Processing',
  'Filtering',
  'Exporting',
];

// This use to map Progress Status Type to particular key which is define in string file for show caption and error string
export const ReportStatusCaptionMap: {[key: string]: string} = {
  Queued: 'queued',
  Processing: 'processing',
  Filtering: 'filtering',
  Exporting: 'exporting',
};

interface ProcessingStepType {
  status: STEP_PROCESSING_STATUS;
  startDate?: string;
  completeDate?: string;
  custodian?: string | null;
}

export interface ProcessingDataSetType {
  dataset: string;
  startDate?: string | null;
  submitDate?: string | null;
  completeDate?: string | null;
  currentStep: PROCESSING_STATUS;
  queued?: ProcessingStepType;
  processing?: ProcessingStepType;
  filtering?: ProcessingStepType;
  exporting?: ProcessingStepType;
  custodian?: string | null;
}

interface Props {
  data: ProcessingDataSetType;
  openSpecificTab?: (type: ReportScreenTabTypes) => void;
  onRefreshData: (data: ProcessingDataSetType) => void;
}

const makeCardExpandedDefault = (data: ProcessingDataSetType) => {
  const curretStep = data.currentStep.toLowerCase() as
    | 'queued'
    | 'processing'
    | 'filtering'
    | 'exporting';
  const currentStateObj = data[curretStep] as ProcessingStepType;
  return (
    currentStateObj.status === 'InProgress' ||
    currentStateObj.status === 'Failed'
  );
};

const ProcessingDataSetInfoCard = ({
  data,
  openSpecificTab,
  onRefreshData,
}: Props) => {
  const {showToast} = useToast();
  const windowSize = useWindowSize();
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();
  const {projectId, workspaceId, mediaEvidenceId, clientId} = useSelector(
    (state: RootState) => state.reports
  );
  const [isExpanded, setIsExpanded] = useState<boolean>(
    makeCardExpandedDefault(data)
  );
  const [isRefreshLoading, setIsRefreshLoading] = useState<boolean>(false);
  const [showExportCSVModal, setShowExportCSVModal] = useState<boolean>(false);
  const [isExportCSVLoading, setIsExportCSVLoading] = useState<boolean>(false);

  const processingStates = useMemo<DataSetReportStatusItem[]>(() => {
    const stepData: DataSetReportStatusItem[] = [];
    PROCESSING_STEP_DISPLAY_SEQUENCE.forEach((key: PROCESSING_STATUS) => {
      if (key.toLowerCase() in data) {
        let objToAdd;
        let toolTipText;
        switch (key) {
          case 'Queued':
            objToAdd = data.queued;
            toolTipText = t(
              'reports.processingSummary.pizzaTrackerProgress.tooltip.queued'
            );
            break;
          case 'Processing':
            objToAdd = data.processing;
            toolTipText = t(
              'reports.processingSummary.pizzaTrackerProgress.tooltip.processing'
            );
            break;
          case 'Filtering':
            objToAdd = data.filtering;
            toolTipText = t(
              'reports.processingSummary.pizzaTrackerProgress.tooltip.filtering'
            );
            break;
          case 'Exporting':
            objToAdd = data.exporting;
            toolTipText = t(
              'reports.processingSummary.pizzaTrackerProgress.tooltip.exporting'
            );
            break;
        }
        stepData.push({
          status: key,
          statusLabel: t(
            `reports.processingSummary.pizzaTrackerProgress.${ReportStatusCaptionMap[key]}`
          ),
          errorLabel: `reports.processingSummary.pizzaTrackerProgress.error.${ReportStatusCaptionMap[key]}`,
          startDate: objToAdd?.startDate,
          completeDate: objToAdd?.completeDate,
          stepProcessingStatus: objToAdd?.status ?? 'NotStarted',
          tooltipInfo: toolTipText,
        });
      } else {
        let toolTipText;
        switch (key) {
          case 'Queued':
            toolTipText = t(
              'reports.processingSummary.pizzaTrackerProgress.tooltip.queued'
            );
            break;
          case 'Processing':
            toolTipText = t(
              'reports.processingSummary.pizzaTrackerProgress.tooltip.processing'
            );
            break;
          case 'Filtering':
            toolTipText = t(
              'reports.processingSummary.pizzaTrackerProgress.tooltip.filtering'
            );
            break;
          case 'Exporting':
            toolTipText = t(
              'reports.processingSummary.pizzaTrackerProgress.tooltip.exporting'
            );
            break;
        }
        stepData.push({
          stepProcessingStatus: 'NotStarted',
          status: key,
          statusLabel: t(
            `reports.processingSummary.pizzaTrackerProgress.${ReportStatusCaptionMap[key]}`
          ),
          errorLabel: `reports.processingSummary.pizzaTrackerProgress.error.${ReportStatusCaptionMap[key]}`,
          tooltipInfo: toolTipText,
        });
      }
    });
    return stepData;
  }, [data]);

  const handleRefreshClick = async () => {
    if (projectId && workspaceId && clientId) {
      const queryParam = {
        clientId: clientId,
        projectId: projectId,
        workspaceId: workspaceId,
        dataset: data.dataset,
      };

      try {
        setIsRefreshLoading(true);
        const response = await getDatasetProcessingProgressReport(
          new URLSearchParams(queryParam).toString()
        );
        const data = _.get(response, 'data.data', []) as ProcessingDataSetType;
        onRefreshData(data);
      } catch (error) {
        showErrorToast(showToast, error);
      } finally {
        setIsRefreshLoading(false);
      }
    }
  };

  const executeCSVExportRequest = async () => {
    if (projectId && workspaceId && clientId) {
      const queryParam = {
        clientId: clientId,
        projectId: projectId,
        workspaceId: workspaceId,
        dataset: data.dataset,
      };
      try {
        setShowExportCSVModal(true);
        setIsExportCSVLoading(true);
        const response = await downloadDataSetProcessingProgressCSV(
          new URLSearchParams(queryParam).toString()
        );
        downloadResponseAsCSV(response, `${data.dataset}_${Date.now()}`);
      } catch (error) {
        showErrorToast(showToast, error);
      } finally {
        setIsExportCSVLoading(false);
      }
    }
    trackGAEvent({
      eventName: GA_EVENT_NAMES.EXPORT,
      click: {
        name: t('gaTracking.reports.exportOption.csv'),
        type: GA_CLICK_TYPES.DROPDOWN,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.reports.exportReport'),
      },
      file_extension: GA_FILE_EXTENTIONS.CSV,
    });
  };

  const handleViewSummaryClick = () => {
    if (mediaEvidenceId)
      dispatch(
        setWorkspaceDataSet({
          workspaceDataSetName: data.dataset,
          worksapceDataSetMediaId: mediaEvidenceId,
          datasetCustodianId: null,
        })
      );
    if (openSpecificTab)
      openSpecificTab(ReportScreenTabTypes.PROCESSED_DATASET);
  };

  const renderHeaderAction = () => {
    return (
      <>
        <AurButton
          type="secondary"
          label={t('common.refresh')}
          size={windowSize.width <= 1024 ? 'Small' : 'Medium'}
          startIcon={<ResetIcon width={24} height={24} aria-hidden="true" />}
          onClick={handleRefreshClick}
          ariaLabel={t(
            'reports.processingInProgress.datasetCard.refreshButtonHint',
            {name: data.dataset}
          )}
          dataTestId="refresh-button"
          showLoader={isRefreshLoading}
        />
        <AurDropdownButton
          elementID="export-dropdown-button"
          dropdownRole="dropdown"
          hintText={t(
            'reports.processingInProgress.datasetCard.exportButtonHint',
            {name: data.dataset}
          )}
          iconType="Other"
          buttonText={t('common.exportReport')}
          classNames={`typo-text ${windowSize.width <= 1024 ? 'buttonSmall_size' : 'buttonMedium_size'}`}
          startIcon={<ExportIcon aria-hidden="true" />}
          type="continue"
          size={windowSize.width <= 1024 ? 'Small' : 'Medium'}
          aria-live="polite"
          iconSize={windowSize.width <= 1024 ? 'small' : 'medium'}
          iconClassName="client-profile-details-dropdown-icon"
          menuDirectionLeft={false}
          menuItemHeight="large"
          menuItemWidth="large"
          menuItemTextSize="large"
          menuClassNames="export-button-drop-down"
          menuItems={[
            {
              label: t('common.exportOption.csv'),
              onClick: () => {
                executeCSVExportRequest();
              },
            },
          ]}
          showLoader={isExportCSVLoading}
        />
      </>
    );
  };

  const renderViewSummaryButton = () => {
    return (
      <AurButton
        type="text"
        size="Medium"
        classNames="view-summary-button-container"
        onClick={handleViewSummaryClick}
        label={t(
          'reports.processingInProgress.datasetCard.viewProcessingSummary'
        )}
        endIcon={<RightArrowIcon width={24} height={24} />}
        ariaLabel={t(
          'reports.processingInProgress.datasetCard.viewSummaryButtonHint',
          {name: data.dataset}
        )}
        dataTestId="view-summary-button"
      />
    );
  };

  const titleContent = (
    <div className="header-title-content-row">
      <div className="header-text-row">
        <Typography
          className="typo-text subhead2_size common-mist-100-text header-text"
          aria-label={`Dataset name ${data.dataset}`}
        >
          {t('reports.processingInProgress.datasetCard.title', {
            value: data.dataset,
          })}
        </Typography>
        <span id="completed-badge-hint" style={{display: 'none'}}>
          {`${t('common.dataset')} ${data.dataset} ${t('common.completedProcess')}`}
        </span>

        {!isExpanded && data.exporting?.completeDate && (
          <div
            className="completed-pile"
            data-testid="completed-badge"
            aria-describedby="completed-badge-hint"
          >
            <CompletedIcon aria-hidden="true" />
            <Typography
              className="typo-text body2_size text"
              aria-label={t('aria.processingCompleted')}
            >
              {t('common.completed')}
            </Typography>
          </div>
        )}
      </div>
      {isExpanded && data.exporting?.completeDate && renderViewSummaryButton()}
    </div>
  );

  const HeaderRightContent: ReactElement | null =
    useMemo((): ReactElement | null => {
      if (isExpanded) {
        return renderHeaderAction();
      } else if (!isExpanded && !data.exporting?.completeDate) {
        return (
          <div
            className="header-right-status-row"
            data-testid="procesing-state-caption-collapsed"
          >
            <Typography
              data-testid="procesing-state-caption-collapse-text"
              className="typo-text subhead4_size common-mist-100-text"
              aria-label={`${t('aria.collapsedDatasetStatus')} ${ReportStatusCaptionMap[data.currentStep]}`}
            >
              {data.currentStep === 'Queued'
                ? t(
                    'reports.processingInProgress.datasetCard.queuedForProcessing'
                  )
                : t(
                    `reports.processingSummary.pizzaTrackerProgress.${ReportStatusCaptionMap[data.currentStep]}`
                  )}
            </Typography>
            <ProcessingIcon />
          </div>
        );
      } else if (!isExpanded && data.exporting?.completeDate) {
        return renderViewSummaryButton();
      }
      return null;
    }, [
      data,
      isExpanded,
      isRefreshLoading,
      isExportCSVLoading,
      clientId,
      projectId,
      workspaceId,
    ]) as ReactElement;

  const renderDate = (
    caption: string,
    date?: string | null,
    testId?: string
  ) => {
    return (
      <div className="date-content-box">
        <Typography
          className="typo-text label2_size date-caption-text"
          aria-label={`Dataset ${t(caption)} for process`}
        >
          {t(caption)}
        </Typography>
        <AurLightTooltip title={date ? t('common.yearMonthDate') : ''}>
          <Typography
            className="typo-text body2_size date-value-text"
            aria-label={
              date
                ? `${t('common.dataset')} ${data.dataset} ${t(caption)} ${t('process.forProcessOn')} ${date ?? '---'}`
                : `${t('common.dataset')} ${data.dataset} ${t('process.not')} ${t(caption)} ${t('process.forProcess')}`
            }
            data-testid={testId}
          >
            {date ? getUTCDateTimeForDisplay(date) : '---'}
          </Typography>
        </AurLightTooltip>
      </div>
    );
  };
  const renderTopDateContent = () => {
    return (
      <div className="top-dataset-dates-container">
        {renderDate(
          'reports.processingInProgress.datasetCard.submittedOn',
          data.submitDate,
          'submitted-date-text'
        )}
        {renderDate(
          'reports.processingInProgress.datasetCard.startedOn',
          data.startDate,
          'started-on-date-text'
        )}
        {renderDate(
          'reports.processingInProgress.datasetCard.completedOn',
          data.exporting?.completeDate,
          'completed-date-text'
        )}
      </div>
    );
  };

  const renderProgressStageContainer = () => {
    const reviewCaption = data.completeDate
      ? 'reports.processingInProgress.datasetCard.readyForReview'
      : `reports.processingInProgress.datasetCard.reviewStatusCaption.${ReportStatusCaptionMap[data.currentStep]}`;
    return (
      <div className="progress-stage-container">
        <Typography
          aria-label={t(reviewCaption)}
          className="typo-text headline3_size review-caption-text"
          data-testid="review-caption-text"
        >
          {t(reviewCaption)}
        </Typography>

        <div
          className="animation-progress-box"
          aria-label={t('aria.processingChart')}
          data-testid="processing-chart-box"
        >
          <AurPizzaTrackerProgress progressSteps={processingStates} />
        </div>
        <Typography
          className="typo-text body4_size bottom-caption-text"
          aria-label={t(
            'reports.processingInProgress.datasetCard.bottomCaption'
          )}
        >
          {t('reports.processingInProgress.datasetCard.bottomCaption')}
        </Typography>
      </div>
    );
  };

  return (
    <>
      <AurAccordion
        title={titleContent}
        titleContent={HeaderRightContent}
        collapsed={!isExpanded}
        className="procesing-data-set-info-card"
        onCollapse={setIsExpanded.bind(this, false)}
        onExpand={setIsExpanded.bind(this, true)}
        accordionAriaLabel={t('accordion.datasetAccordionLabel', {
          dataset: data.dataset,
        })}
        toggleButtonTestId="toggle-card-button"
        alertWarningIcon={false}
        dataTestId={`processing-data-set-info-${data.dataset}`}
        toggleButtonExpandHint={t('button.expandProcessingReportHint', {
          dataset: data.dataset,
        })}
        toggleButtonCollapseHint={t('button.collapseProcessingReportHint', {
          dataset: data.dataset,
        })}
      >
        {renderTopDateContent()}
        <div className="separator" />
        {renderProgressStageContainer()}
      </AurAccordion>
      {showExportCSVModal && (
        <AurPopup
          className="aur-unsaved-changes-popup"
          open={showExportCSVModal}
          onCancel={() => setShowExportCSVModal(false)}
          onOk={() => setShowExportCSVModal(false)}
          popupHeading={t('common.csvDownloadPopup.heading')}
          popupContent={t('common.csvDownloadPopup.content')}
          showPopupHeading={true}
          showPopupTitle={true}
          showCloseIcon={true}
          labelActionOk={t('common.csvDownloadPopup.labelActionOk')}
          showCancelOption={false}
          buttonStyle="stacked"
          cancelButtonTitle=""
        />
      )}
    </>
  );
};

export default ProcessingDataSetInfoCard;
